import React, { useCallback, useContext, useMemo } from 'react'

import UploadedFile from './RequestedFileCell/UploadedFile'
import FileStatusCell from './FileStatusCell'
import DeleteFile from './DeleteFile'
import ActionsDropdown from '../../../../../../../features/components/ActionsDropdown'

import { RequestedFilesContext } from '../RequestedFilesContext'

import { useHandleDeleteUploadFile } from '../hooks'
import useGetAccessibleUrl from '../../../../../../../features/hooks/useGetAccessibleUrl'

import { tableColumnsSize, uploadRemoveStyle } from '../../const/columns'
import { useGetDropdownOptions } from './useDropdownOptions'

export const useUploadedFilesColumns = ({ openUploadFileForm, periodStartColumn }) => {
  const { handleFileDownloading } = useGetAccessibleUrl()
  const { handleShowDisapprovalModal, handleStatusChange } = useContext(RequestedFilesContext)

  const handleFileDownload = useCallback(
    uploadedFile => () => {
      handleFileDownloading({
        fileData: {
          url: uploadedFile?.file_url,
          file_name: uploadedFile?.name
        }
      })
    },
    [handleFileDownloading]
  )

  const handleDelete = useHandleDeleteUploadFile()

  const getDropdownOptions = useGetDropdownOptions({
    handleDelete,
    openUploadFileForm,
    handleFileDownload,
    handleShowDisapprovalModal,
    handleStatusChange
  })

  const uploadedFilesColumns = useMemo(
    () => [
      {
        header: 'Requested file',
        Cell: ({ requested_file, uploaded_file, uploaded_files }) => {
          // the columns model is used for multiple files or one, once one the uploaded_file is not passed, so only one first is available
          const uploadedFile = uploaded_file || uploaded_files[0]

          return (
            <UploadedFile
              requestedFile={requested_file}
              uploadedFile={uploadedFile}
              handleFileDownloading={handleFileDownloading}
            />
          )
        }
      },
      periodStartColumn,
      {
        header: 'Due date',
        Cell: () => '',
        style: { maxWidth: tableColumnsSize.dueDate }
      },
      {
        header: 'Status',
        Cell: ({ requested_file, uploaded_file, uploaded_files }) => {
          // the columns model is used for multiple files or one, once one the uploaded_file is not passed, so only one first is available
          const uploadedFile = uploaded_file || uploaded_files[0]
          // check if file is floated(not linked to requested file, custom date or etc..)
          const isFloatedFile = !!requested_file.note_code

          if (isFloatedFile) {
            return null
          }

          return (
            <FileStatusCell
              uploadedFile={uploadedFile}
              requirementFileId={requested_file.id}
              handleShowDisapprovalModal={handleShowDisapprovalModal}
              handleStatusChange={handleStatusChange}
            />
          )
        },
        style: { width: tableColumnsSize.status, minWidth: tableColumnsSize.status }
      },
      {
        Cell: ({ id, requestedRowId, requested_file, uploaded_file, uploaded_files }) => {
          // the columns model is used for multiple files or one, once one the uploaded_file is not passed, so only one first is available
          const uploadedFile = uploaded_file || uploaded_files[0]

          return (
            <DeleteFile
              // for multiple files, the id should be taken from the parent row
              requestedFileRowId={requestedRowId || id}
              // check if file is floated(not linked to requested file, custom date or etc..)
              isFloatedFile={!!requested_file.note_code}
              uploadedFile={uploadedFile}
              mediaId={requested_file.booked_media_id}
              deadline={requested_file.deadline_date}
              handleDelete={handleDelete}
            />
          )
        },
        style: uploadRemoveStyle
      },
      {
        Cell: ({ id, requestedRowId, requested_file, uploaded_file, uploaded_files }) => {
          const requestedFileId = requested_file.id
          const bookedMediaId = requested_file.booked_media_id
          // the columns model is used for multiple files or one, once one the uploaded_file is not passed, so only one first is available
          const uploadedFile = uploaded_file || uploaded_files[0]
          // check if file is floated(not linked to requested file, custom date or etc..)
          const isFloatedFile = !!requested_file.note_code

          return (
            <ActionsDropdown
              itemId={requestedFileId}
              closeOnInsideClick={true}
              options={getDropdownOptions({
                isFloatedFile,
                // for multiple files, the id should be taken from the parent row
                requestedFileRowId: requestedRowId || id,
                requestedFile: requested_file,
                mediaId: bookedMediaId,
                uploadedFile
              })}
            />
          )
        },
        style: { maxWidth: tableColumnsSize.actions }
      }
    ],
    [
      getDropdownOptions,
      handleDelete,
      handleFileDownloading,
      handleShowDisapprovalModal,
      handleStatusChange,
      periodStartColumn
    ]
  )

  return uploadedFilesColumns
}
